import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'

import ContentWarningIcon from '../../../mini/ContentWarningIcon'
import H5 from '../../../typography/headers/H5'
import Caption1 from '../../../typography/captions/Caption1'
import Caption4 from '../../../typography/captions/Caption4'
import MarkdownLabel from '../../../mini/MarkdownLabel'

import { getViewResourceIcon } from '../../../../utils/resources'
import { mapContentWarnings } from '../../../../utils/contentwarnings'
import {
  ResourceIncomplete,
  resourceHasQMSpecificInfo
} from '../../../../utils/resources'
import { fancyTimeFormat } from '../../../../utils/time'

const ResourceContainer = styled.li`
  flex-wrap: wrap;
  width: 48%;
  margin: 20px 0 0;
  cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  background: #fff;
  border-radius: 5px;
  position: relative;
  z-index: 0;

  a {
    color: unset;
    cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  }

  .content-warning-icon {
    height: 27px;
    width: 27px;
    margin-left: 7px;
    display: inline-table;

    .abbrev {
      font-size: 12px;
      font-weight: 500;
      color: #4a4a4a;
    }
  }

  &:hover .resource-body {
    background-color: ${props => (props.isClickable ? '#F5F5F5' : '#fff')};
  }

  label {
    cursor: ${props => (props.isClickable ? 'pointer' : 'default')};
  }

  .content-warning-icon {
    margin: 0 0 0 5px;
  }
`

const ResourceHeader = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 70px;
  background-image: ${props =>
    props.thumbnail
      ? 'linear-gradient(0deg, rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(' +
        props.thumbnail +
        ') !important'
      : 'none'};
  background-size: 100%;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
`

const ResourceBody = styled.div`
  padding: 10px 20px 15px;
  background: #fff;
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
  box-shadow: 0 1px 9px 0 rgba(0, 0, 0, 0.08);
  z-index: 1;
  position: relative;
`

const ResourceContent = styled.div`
  width: 100%;
  margin: 0;
  padding: 2%;
  text-align: left;
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
`

const ResourceDescription = styled(Caption1)`
  padding-top: 7px;
  overflow-wrap: break-word;
  line-height: 1.2 !important;

  label {
    line-height: unset !important;
    font-size: unset !important;
  }
`

const QMSpecificData = styled.div`
  border-bottom-left-radius: 7px;
  border-bottom-right-radius: 7px;
  background-color: #e9f4f8;
  padding: 12px 12px 12px;
  position: relative;
  bottom: 7px;
`

const QMSpecificDataRow = styled.div`
  padding-top: 8px;
  line-height: 16px;
`

const ResourceItem = props => (
  <ResourceContainer isClickable={props.isClickable}>
    <a
      onClick={e =>
        props.isClickable
          ? props.handleResourceClick(props.resource, props.activity)
          : e.preventDefault()
      }
    >
      <ResourceHeader
        className="header"
        thumbnail={props.resource.thumbnail_url}
      >
        {getViewResourceIcon(props.resource.resource_type)}
      </ResourceHeader>
      <ResourceBody className="resource-body">
        <ResourceContent>
          <H5>{props.resource.name}</H5>
          {props.resource.description && props.resource.description.length > 0 && (
            <ResourceDescription>
              <MarkdownLabel value={props.resource.description} />
            </ResourceDescription>
          )}
        </ResourceContent>
      </ResourceBody>
      {(resourceHasQMSpecificInfo(props.resource) ||
        props.resource.isResourceIncomplete) && (
        <QMSpecificData>
          {props.resource.start_time || props.resource.end_time ? (
            <QMSpecificDataRow
              style={{ display: 'flex', alignItems: 'baseline' }}
            >
              <Caption4>Start Time:&nbsp;</Caption4>
              {props.resource.start_time ? (
                <Caption1 style={{ marginRight: '18px' }}>
                  {fancyTimeFormat(props.resource.start_time)}
                </Caption1>
              ) : (
                <Caption1 style={{ marginRight: '18px' }}>No Change</Caption1>
              )}
              <Caption4>End Time:&nbsp;</Caption4>
              {props.resource.end_time ? (
                <Caption1 style={{ marginRight: '18px' }}>
                  {fancyTimeFormat(props.resource.end_time)}
                </Caption1>
              ) : (
                <Caption1 style={{ marginRight: '18px' }}>No Change</Caption1>
              )}
            </QMSpecificDataRow>
          ) : null}

          {props.resource.content_warnings &&
            Object.keys(props.resource.content_warnings).length > 0 && (
              <QMSpecificDataRow>
                <Caption4>
                  Content Warnings:&nbsp;
                  {mapContentWarnings(props.resource.content_warnings).map(
                    cw => (
                      <ContentWarningIcon
                        value={cw.value}
                        text={cw.text}
                        abbrev={cw.abbrev}
                        showTooltip
                      />
                    )
                  )}
                </Caption4>
              </QMSpecificDataRow>
            )}

          {props.resource.copyright_holder && (
            <QMSpecificDataRow>
              <Caption4>
                I or someone in my organization made this resource.
              </Caption4>
            </QMSpecificDataRow>
          )}
          {props.resource.file_name && props.resource.license_citation && (
            <QMSpecificDataRow>
              <Caption4>
                Someone outside my organization made this resource.
              </Caption4>
              <QMSpecificDataRow>
                <Caption1 style={{ wordBreak: 'break-word' }}>
                  Citation:&nbsp;{props.resource.license_citation}
                </Caption1>
              </QMSpecificDataRow>
            </QMSpecificDataRow>
          )}
          {props.resource.isResourceIncomplete && <ResourceIncomplete />}
        </QMSpecificData>
      )}
    </a>
  </ResourceContainer>
)

ResourceItem.propTypes = {
  resource: PropTypes.object.isRequired,
  handleResourceClick: PropTypes.func.isRequired,
  activity: PropTypes.object.isRequired,
  isClickable: PropTypes.bool.isRequired
}

export default ResourceItem



// WEBPACK FOOTER //
// ./src/components/quest/activity/resources/ResourceItem.js